import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  PROJECT_TYPE_FILTER_OPTIONS,
  PROJECT_TYPE_SOURCE_IDS,
  fetchProjectDetails,
  getLatestServerDate,
  getProjectSourceId,
  getProjectTypeFilterValue,
  getProjectsByTypeKey,
  refreshServerDate,
  resolveProjectsForType,
} from "./ddApi";
import { calcHours, emptyWorklogRow, extractTime, todayStr } from "./worklogHelpers";
import { ClockIcon, CloseIcon, DurationBadge, Field, PresetPills, ProjectSearchInput, Sel, TimeSel, TrashIcon } from "./WorkLogShared";
import "./taskPlanner.css";

const UpdateGroupModal = ({
  isOpen,
  onClose,
  onSave,
  onSaveWorklog,
  projects = [],
  isEdit,
  initialData = {},
}) => {
  const [date, setDate] = useState("");
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [detailCache, setDetailCache] = useState({});
  const [loadingDetail, setLoadingDetail] = useState({});
  const [typedProjectCache, setTypedProjectCache] = useState({});
  const [loadingTypedProjects, setLoadingTypedProjects] = useState({});
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [serverDate, setServerDate] = useState(null);
  const [dateTampered, setDateTampered] = useState(false);
  const [checkingClock, setCheckingClock] = useState(true);

  const isAnyDropdownOpen = Object.values(openDropdowns).some(Boolean);
  const hasSourceMeta = useMemo(
    () => (projects || []).some((p) => getProjectSourceId(p)),
    [projects]
  );

  const ensureProjectDetail = useCallback(async (projectId) => {
    if (!projectId || detailCache[projectId] || loadingDetail[projectId]) return;
    try {
      const result = await fetchProjectDetails(projectId);
      setDetailCache((prev) => ({ ...prev, [projectId]: result }));
    } catch {
      // ignore
    }
  }, [detailCache, loadingDetail]);

  useEffect(() => {
    if (!isOpen || hasSourceMeta) return;
    const keys = [...new Set(rows.map((r) => r.projectType).filter((k) => k && k !== "all"))];
    keys.forEach((typeKey) => {
      if (typedProjectCache[typeKey] || loadingTypedProjects[typeKey]) return;
      setLoadingTypedProjects((prev) => ({ ...prev, [typeKey]: true }));
      getProjectsByTypeKey(typeKey)
        .then((list) => setTypedProjectCache((prev) => ({ ...prev, [typeKey]: Array.isArray(list) ? list : [] })))
        .catch(() => setTypedProjectCache((prev) => ({ ...prev, [typeKey]: [] })))
        .finally(() => setLoadingTypedProjects((prev) => ({ ...prev, [typeKey]: false })));
    });
  }, [isOpen, rows, hasSourceMeta, typedProjectCache, loadingTypedProjects]);

  useEffect(() => {
    if (!isOpen || !initialData) return;
    setDate(initialData.date && initialData.date !== "Unscheduled" ? initialData.date : todayStr());
    if (initialData.tasks && initialData.tasks.length > 0) {
      const mappedRows = initialData.tasks.map((task) => {
        const pid = task.project_id || task.project || "";
        const listed = projects.find((p) => (p.id || p.project_id) === pid);
        const inferredType = getProjectTypeFilterValue(task);
        return {
          _id: crypto.randomUUID(),
          taskId: task.id,
          projectType: inferredType !== "all" ? inferredType : getProjectTypeFilterValue(listed),
          projectId: pid,
          activityId: task.activity_id || "",
          subActivityId: task.subactivity_id || "",
          startTime: extractTime(task.start_time),
          endTime: extractTime(task.end_time),
          workType: task.work_type || "",
          description: task.note || "",
          isSelected: false,
          status: task.status || "not_done",
        };
      });
      setRows(mappedRows);
      [...new Set(mappedRows.map((r) => r.projectId).filter(Boolean))].forEach((pid) => ensureProjectDetail(pid));
    } else {
      setRows([emptyWorklogRow()]);
    }
    setErrors({});
  }, [isOpen, initialData, ensureProjectDetail, projects]);

  useEffect(() => {
    if (!isOpen) return;
    setCheckingClock(true);
    refreshServerDate().then(() => {
      const trueNow = getLatestServerDate();
      if (!trueNow) {
        setServerDate(new Date());
        setDateTampered(false);
        setCheckingClock(false);
        return;
      }
      const driftMs = Math.abs(new Date().getTime() - trueNow.getTime());
      setDateTampered(driftMs > 2 * 60 * 1000);
      setServerDate(trueNow);
      setCheckingClock(false);
    });
  }, [isOpen]);

  const maxSelectableDate = useMemo(() => {
    if (!serverDate) return null;
    const d = serverDate;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, [serverDate]);

  useEffect(() => {
    if (maxSelectableDate && date && date > maxSelectableDate) setDate(maxSelectableDate);
  }, [maxSelectableDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const detailFor = (pid) => detailCache[pid] || null;
  const activitiesFor = (pid) =>
    [...(detailFor(pid)?.activities_detail || [])].sort(
      (a, b) => (Number(a.sorting_var) || 0) - (Number(b.sorting_var) || 0)
    );
  const subActivitiesFor = (pid, aid) => {
    const act = activitiesFor(pid).find((a) => String(a.id) === String(aid));
    return [...(act?.subactivities || [])].sort(
      (a, b) => (Number(a.sorting_var) || 0) - (Number(b.sorting_var) || 0)
    );
  };
  const workTypesFor = (pid) => detailFor(pid)?.sector_detail?.stage_work_types || [];

  const updateRow = useCallback((id, field, value) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r._id !== id) return r;
        const u = { ...r, [field]: value };
        if (field === "projectId") {
          u.activityId = "";
          u.subActivityId = "";
          u.workType = "";
        }
        if (field === "activityId") u.subActivityId = "";
        if (field !== "isSelected") u.isSelected = true;
        return u;
      })
    );
    setErrors((prev) => {
      const n = { ...prev };
      delete n[`${id}.${field}`];
      return n;
    });
  }, []);

  const handleProjectTypeChange = useCallback((rowId, typeKey) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r._id !== rowId) return r;
        const sourceId = PROJECT_TYPE_SOURCE_IDS[typeKey];
        const current = projects.find((p) => (p.id || p.project_id) === r.projectId);
        const currentSource = getProjectSourceId(current);
        const keepProject = !r.projectId || !sourceId || !currentSource || currentSource === sourceId;
        if (keepProject) return { ...r, projectType: typeKey, isSelected: true };
        return { ...r, projectType: typeKey, projectId: "", activityId: "", subActivityId: "", workType: "", isSelected: true };
      })
    );
  }, [projects]);

  const handleProjectChange = useCallback((rowId, pid) => {
    const selected = projects.find((p) => (p.id || p.project_id) === pid);
    const inferredType = selected ? getProjectTypeFilterValue(selected) : "all";
    setRows((prev) =>
      prev.map((r) => {
        if (r._id !== rowId) return r;
        return {
          ...r,
          projectId: pid,
          projectType: pid && inferredType !== "all" ? inferredType : r.projectType,
          activityId: "",
          subActivityId: "",
          workType: "",
          isSelected: true,
        };
      })
    );
    if (pid) ensureProjectDetail(pid);
  }, [projects, ensureProjectDetail]);

  const applyPreset = useCallback((rowId, start, end) => {
    setRows((prev) => prev.map((r) => (r._id === rowId ? { ...r, startTime: start, endTime: end, isSelected: true } : r)));
  }, []);

  const addRow = () => setRows((prev) => [...prev, emptyWorklogRow()]);
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r._id !== id));
  const totalHours = rows.reduce((sum, r) => sum + calcHours(r.startTime, r.endTime), 0);

  const validate = () => {
    const errs = {};
    rows.forEach((r) => {
      if (!r.projectId) errs[`${r._id}.projectId`] = true;
      if (!r.activityId) errs[`${r._id}.activityId`] = true;
      if (!r.subActivityId) errs[`${r._id}.subActivityId`] = true;
      if (!r.startTime) errs[`${r._id}.startTime`] = true;
      if (!r.endTime) errs[`${r._id}.endTime`] = true;
      if (r.startTime && r.endTime && r.endTime <= r.startTime) errs[`${r._id}.endTime`] = true;
      if (!r.workType) errs[`${r._id}.workType`] = true;
    });
    if (!date) errs.date = "Date is required";
    if (totalHours > 9) errs.totalHours = `Total work hours (${totalHours.toFixed(2)} hrs) cannot exceed 9 hrs`;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = rows.map((row) => ({
        id: row.taskId,
        project: row.projectId,
        subactivity: row.subActivityId,
        date,
        start_time: row.startTime,
        end_time: row.endTime,
        work_type: row.workType,
        note: row.description,
      }));
      const worklogPayload = rows.filter((r) => r.isSelected).map((row) => ({
        id: row.taskId,
        project: row.projectId,
        subactivity: row.subActivityId,
        date,
        start_time: row.startTime,
        end_time: row.endTime,
        work_type: row.workType,
        note: row.description,
      }));
      if (!isEdit) await onSaveWorklog(date, worklogPayload);
      else await onSave(date, payload);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const selectableRows = rows.filter((r) => r.status?.toUpperCase() !== "COMPLETED");
  const areAllSelected = selectableRows.length > 0 && selectableRows.every((r) => r.isSelected);

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      centered
      backdrop="static"
      className="tp-worklog-overlay"
      dialogClassName="tp-worklog-dialog"
      contentClassName="tp-worklog-content"
      backdropClassName="tp-worklog-backdrop"
    >
      <div className="tp-modal-head">
        <div className="tp-modal-title-wrap">
          <span className="tp-modal-icon"><ClockIcon /></span>
          <div>
            <h3>{isEdit ? "Update Planned Tasks" : "Log Selected Tasks"}</h3>
            <p>{isEdit ? "Edit today's planned tasks · max 9 hours" : "Select the tasks you want to log"}</p>
          </div>
        </div>
        <div className="tp-modal-head-right">
          <label className={`tp-date-field ${errors.date ? "error" : ""}`}>
            <span>Date *</span>
            <input
              type="date"
              value={date}
              max={maxSelectableDate || todayStr()}
              disabled={dateTampered || checkingClock}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>
          <button type="button" className="tp-close-btn" onClick={onClose} title="Close">
            <CloseIcon />
          </button>
        </div>
      </div>
      {dateTampered && (
        <div className="tp-warn-banner">
          Your device's date/time appears incorrect. Please correct your system date to continue.
        </div>
      )}

      <div className={`tp-modal-body ${isAnyDropdownOpen ? "dropdown-open" : ""}`}>
        {!isEdit && (
          <div className="tp-log-toolbar">
            <label>
              <input
                type="checkbox"
                checked={areAllSelected}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setRows((prev) =>
                    prev.map((r) => ({
                      ...r,
                      isSelected: r.status?.toUpperCase() === "COMPLETED" ? false : checked,
                    }))
                  );
                }}
              />
              Select all
            </label>
          </div>
        )}
        <div className="tp-log-list">
          {rows.map((row, idx) => {
            const isLoadingThis = !!loadingDetail[row.projectId];
            const activities = activitiesFor(row.projectId);
            const subActivities = subActivitiesFor(row.projectId, row.activityId);
            const workTypes = workTypesFor(row.projectId);
            const timeInvalid = row.startTime && row.endTime && row.endTime <= row.startTime;
            const e = (f) => !!errors[`${row._id}.${f}`];
            const disabled = !isEdit;
            const typeFilteredProjects = resolveProjectsForType({
              projects,
              typeKey: row.projectType || "all",
              includeProjectId: row.projectId,
              hasSourceMeta,
              remoteList: typedProjectCache[row.projectType],
            });
            return (
              <div
                key={row._id}
                className={`tp-log-card ${openDropdowns[row._id] ? "is-open" : ""} ${disabled ? "is-readonly" : ""}`}
              >
                {!isEdit ? (
                  <input
                    type="checkbox"
                    className="tp-log-check"
                    checked={row.isSelected}
                    disabled={row.status?.toUpperCase() === "COMPLETED"}
                    onChange={(ev) => updateRow(row._id, "isSelected", ev.target.checked)}
                  />
                ) : (
                  <span className="tp-row-num">{idx + 1}</span>
                )}
                <div className="tp-log-main">
                  <div className="tp-log-row tp-log-row-top">
                    <Field label="Project type">
                      <Sel value={row.projectType || "all"} onChange={(v) => handleProjectTypeChange(row._id, v)} disabled={disabled} hidePlaceholder>
                        {PROJECT_TYPE_FILTER_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </Sel>
                    </Field>
                    <Field label="Project" required>
                      <ProjectSearchInput
                        projects={typeFilteredProjects}
                        value={row.projectId}
                        onChange={(pid) => handleProjectChange(row._id, pid)}
                        error={e("projectId")}
                        disabled={disabled}
                        onToggle={(open) => setOpenDropdowns((prev) => ({ ...prev, [row._id]: open }))}
                      />
                    </Field>
                    <Field label="Activity" required>
                      <Sel value={row.activityId} onChange={(v) => updateRow(row._id, "activityId", v)} placeholder="Select activity" disabled={!row.projectId || isLoadingThis || disabled} loading={isLoadingThis} error={e("activityId")}>
                        {activities.map((a) => <option key={a.id} value={a.id}>{a.activity_name}</option>)}
                      </Sel>
                    </Field>
                    <Field label="Sub-activity" required>
                      <Sel value={row.subActivityId} onChange={(v) => updateRow(row._id, "subActivityId", v)} placeholder="Select sub-activity" disabled={!row.activityId || isLoadingThis || disabled} error={e("subActivityId")}>
                        {subActivities.map((s) => (
                          <option key={s.id} value={s.id}>{s.sorting_var ? `Stage ${s.sorting_var} – ` : ""}{s.subactivity_name}</option>
                        ))}
                      </Sel>
                    </Field>
                    <Field label="Work type">
                      <Sel value={row.workType} onChange={(v) => updateRow(row._id, "workType", v)} placeholder="Select type" disabled={disabled} loading={isLoadingThis} error={e("workType")}>
                        {workTypes.map((wt) => <option key={wt.id} value={wt.id}>{wt.name}</option>)}
                      </Sel>
                    </Field>
                  </div>
                  <div className="tp-log-row tp-log-row-bottom">
                    <Field label="Start" required>
                      <TimeSel value={row.startTime} onChange={(v) => updateRow(row._id, "startTime", v)} placeholder="In" error={e("startTime")} disabled={disabled} />
                    </Field>
                    <Field label="End" required>
                      <TimeSel value={row.endTime} onChange={(v) => updateRow(row._id, "endTime", v)} placeholder="Out" error={e("endTime") || (!disabled && timeInvalid)} disabled={disabled} />
                    </Field>
                    <Field label="Hours">
                      <div className="tp-preset-row">
                        <PresetPills onApply={(s, end) => applyPreset(row._id, s, end)} disabled={disabled} />
                        <DurationBadge start={row.startTime} end={row.endTime} />
                      </div>
                    </Field>
                    <Field label="Description">
                      <input
                        type="text"
                        className="tp-input tp-note-input"
                        value={row.description}
                        disabled={disabled}
                        onChange={(ev) => updateRow(row._id, "description", ev.target.value)}
                        placeholder="What did you work on?"
                      />
                    </Field>
                  </div>
                </div>
                {isEdit && (
                  <button type="button" className="tp-delete-btn" onClick={() => removeRow(row._id)} disabled={checkingClock || dateTampered} title="Remove row">
                    <TrashIcon />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {isEdit && (
        <div className="tp-add-row-wrap">
          <button type="button" className="tp-add-row" onClick={addRow}>+ Add more tasks</button>
        </div>
      )}

      {Object.keys(errors).length > 0 && (
        <div className="tp-error-banner">
          {errors.totalHours || "Please fill in all required fields correctly for the selected rows."}
        </div>
      )}

      <div className="tp-modal-foot">
        <div className={`tp-total ${totalHours > 9 ? "over" : ""}`}>
          <span>Total duration</span>
          <strong>{totalHours > 0 ? `${totalHours.toFixed(2)} hrs` : "0.00 hrs"}</strong>
        </div>
        <div className="tp-foot-actions">
          <button type="button" className="tp-btn tp-btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="tp-btn tp-btn-primary" onClick={handleSave} disabled={saving || checkingClock || dateTampered}>
            {saving ? "Updating…" : isEdit ? "Update selected" : "Log selected"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default UpdateGroupModal;
